import { Banner } from '@components/display/banners';
import { CenteredEmptyState } from '@components/display/emptyStates';
import { useMaxWidthWrapperScrollElement } from '@components/layout/MaxWidthWrapper';
import { Tabs } from '@components/utility/tabs';
import { isInfiniteLoadingItem, useInfiniteLazyLoader } from '@hooks/useInfiniteLazyLoader';
import { Box, Stack } from '@mui/material';
import { OnlineTrainingListCard } from '@onlineTraining/components/display/cards/OnlineTrainingListCard';
import {
    DEFAULT_ACTIVE_TAB,
    ONLINE_TRAINING_BANNER,
    ONLINE_TRAINING_CATEGORY_TABS,
    PAGE_SIZE,
} from '@onlineTraining/constants/onlineTraining';
import { useLazyGetOnlineTrainingCoursesQuery } from '@onlineTraining/services/onlineTraining';
import { OnlineCourseCategory, OnlineTrainingCourseListItem } from '@onlineTraining/types/onlineTraining';
import { SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSearchParams } from 'react-router-dom';

const TABS_TOP_SPACING_PX = 32;
const GRADIENT_FADE_DISTANCE_PX = 100;

const onlineTrainingBanner = {
    background: ONLINE_TRAINING_BANNER.background,
    header_title: (
        <FormattedMessage
            {...ONLINE_TRAINING_BANNER.headerTitle}
            values={{ blue: (chunks) => <span className="text-primary-600">{chunks}</span> }}
        />
    ),
    header_superscript: <FormattedMessage {...ONLINE_TRAINING_BANNER.headerSuperscript} />,
    image: ONLINE_TRAINING_BANNER.image,
    image_alt: ONLINE_TRAINING_BANNER.imageAlt,
};

const OnlineTrainingList = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const categoryParam = searchParams.get('category');
    const isValidCategory = ONLINE_TRAINING_CATEGORY_TABS.some((tab) => tab.value === categoryParam);
    const selectedCategory = isValidCategory ? (categoryParam as OnlineCourseCategory | 'all') : DEFAULT_ACTIVE_TAB;
    const containerRef = useRef<HTMLDivElement | null>(null);
    const scrollElement = useMaxWidthWrapperScrollElement();
    const [gradientOpacity, setGradientOpacity] = useState(0);

    const [loadCoursesTrigger, { isError }] = useLazyGetOnlineTrainingCoursesQuery();

    const loadCoursesPage = useCallback(
        async ({ page, pageSize }: { page: number; pageSize: number }) => {
            const pageDataResponse = await loadCoursesTrigger(
                {
                    page,
                    page_size: pageSize,
                    category: selectedCategory === 'all' ? undefined : selectedCategory,
                    ordering: 'online_course__sorting_weight',
                },
                true
            ).unwrap();

            return pageDataResponse;
        },
        [loadCoursesTrigger, selectedCategory]
    );

    const {
        items: courses,
        sentinelRef,
        hasRealItems,
        isLoadingMore,
    } = useInfiniteLazyLoader<OnlineTrainingCourseListItem>({
        pageSize: PAGE_SIZE,
        scrollElement,
        contextKey: `online-training:${selectedCategory}`,
        loadPage: loadCoursesPage,
        getItemKey: (course) => course.id,
        onError: (error) => {
            console.error('Error loading online training courses:', error);
        },
    });

    useEffect(() => {
        if (!scrollElement || !containerRef.current) {
            return;
        }

        const updateGradientOpacity = () => {
            if (!containerRef.current) {
                return;
            }

            const rootTop = scrollElement.getBoundingClientRect().top;
            const containerTop = containerRef.current.getBoundingClientRect().top;
            const stickyScrollDistance = Math.max(0, rootTop - containerTop - TABS_TOP_SPACING_PX);
            const nextOpacity = Math.min(stickyScrollDistance / GRADIENT_FADE_DISTANCE_PX, 1);

            setGradientOpacity((currentOpacity) =>
                Math.abs(currentOpacity - nextOpacity) < 0.01 ? currentOpacity : nextOpacity
            );
        };

        updateGradientOpacity();
        scrollElement.addEventListener('scroll', updateGradientOpacity, { passive: true });
        window.addEventListener('resize', updateGradientOpacity);

        return () => {
            scrollElement.removeEventListener('scroll', updateGradientOpacity);
            window.removeEventListener('resize', updateGradientOpacity);
        };
    }, [scrollElement]);

    const handleTabChange = (_: SyntheticEvent, newValue: OnlineCourseCategory | 'all') => {
        setSearchParams(
            (params) => {
                if (newValue === DEFAULT_ACTIVE_TAB) {
                    params.delete('category');
                } else {
                    params.set('category', newValue);
                }

                return params;
            },
            { preventScrollReset: true, replace: false }
        );
    };

    return (
        <Stack ref={containerRef} useFlexGap direction="column" paddingBottom={10} pt={`${TABS_TOP_SPACING_PX}px`}>
            <Box px={{ xs: 2, md: 8 }}>
                <Banner className="!mt-0 !px-0" data={onlineTrainingBanner} />
            </Box>

            <Box position="sticky" top={0} zIndex={2} bgcolor="secondary-alt.light" px={{ xs: 2, md: 8 }} py={1}>
                <Tabs
                    value={selectedCategory}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={{
                        minHeight: 0,
                        '& .MuiTabs-flexContainer': { gap: 1 },
                    }}
                >
                    {ONLINE_TRAINING_CATEGORY_TABS.map((tab) => (
                        <Tabs.Tab key={tab.value} value={tab.value} label={<FormattedMessage {...tab.label} />} />
                    ))}
                </Tabs>
                {hasRealItems || isLoadingMore ? (
                    <Box
                        aria-hidden
                        position="absolute"
                        left={0}
                        top="100%"
                        width="100%"
                        height="75px"
                        sx={{
                            pointerEvents: 'none',
                            opacity: gradientOpacity,
                            background: 'linear-gradient(180deg, #FAF9F5, rgba(250, 249, 245, 0.00))',
                        }}
                    />
                ) : null}
            </Box>

            <Stack px={{ xs: 2, md: 8 }} pt="24px" gap={4}>
                {hasRealItems || isLoadingMore ? (
                    <Stack spacing={2.5} id="courses">
                        {courses.map((course, index) =>
                            isInfiniteLoadingItem(course) ? (
                                <OnlineTrainingListCard key={`skeleton-${index}`} loading />
                            ) : (
                                <OnlineTrainingListCard key={course.id} course={course} />
                            )
                        )}
                    </Stack>
                ) : isError ? (
                    <CenteredEmptyState
                        message={
                            <FormattedMessage
                                id="onlineTraining.error"
                                defaultMessage="Unable to load courses right now."
                            />
                        }
                    />
                ) : (
                    <CenteredEmptyState
                        message={
                            <FormattedMessage
                                id="onlineTraining.noCourses"
                                defaultMessage="No courses available for this category."
                            />
                        }
                    />
                )}

                <div ref={sentinelRef} />
            </Stack>
        </Stack>
    );
};

export { OnlineTrainingList };
